import stateCacheService from './StateCache';
import storeManager from '../store/StoreManager';
import correctResponsesActions from '../actions/CorrectResponsesActions';
import candidateProgressConstants from '../constants/CandidateProgressConstants';
import parentTestService from './ParentTestService';
import apiService from './api/ApiService';
import sessionReader from '../store/readHelpers/SessionReader';
import navigationService from './NavigationService';
import sessionActions from '../actions/SessionActions';
import sessionConstants from '../constants/SessionConstants';
import utilService from './UtilService';
import readinessCheckActions from '../actions/ReadinessCheckActions';
import pendingAssessmentsActions from '../actions/PendingAssessmentsActions';
import settingsActions from '../actions/SettingsActions';
import assessmentActions from '../actions/AssessmentActions';
import responseActions from '../actions/ResponseActions';
import questionReader from '../store/readHelpers/QuestionReader';

const readinessCheckService = {
    isReadinessCheckEnabledButIncomplete() {
        return sessionReader.hasReadinessCheck() && !this.isReadinessCheckComplete();
    },

    isReadinessCheckComplete() {
        const { readinessCheck } = storeManager.getState();
        return readinessCheck.isReadinessCheckComplete;
    },

    setReadinessCheckComplete(contentRevisionId = sessionReader.getReadinessCheckContentRevisionId()) {
        const stateCacheInstance = stateCacheService.getStateCacheInstance();
        // Store in local storage so that the user does not take the test again if resuming on the same device
        stateCacheInstance.setReadinessCheckComplete(contentRevisionId);
        storeManager.dispatch(
            readinessCheckActions.setReadinessCheckComplete(true)
        );
        this.cleanUpResponseDataAfterReadinessCheck();
        this.reportDoneReadinessCheckToMonitor();

        parentTestService.unsetChildIdentifiers();

        if (!sessionReader.isParentTest() && !sessionReader.isRemoteOralExam()) {
            readinessCheckService.restoreStandaloneQuestionSet();
        }
    },

    cleanUpResponseDataAfterReadinessCheck() {
        const stateCacheInstance = stateCacheService.getStateCacheInstance();
        // Remove correct responses loaded for client side evaluation of questions in the readiness check
        storeManager.dispatch(correctResponsesActions.clearCorrectResponses());
        // Remove the stored responses for readiness check in state and local cache
        const allQuestionsInReadinessCheck = questionReader.getAllQuestionIdsFlat();
        allQuestionsInReadinessCheck.forEach((contentRevisionId) => {
            stateCacheInstance.removeQuestionResponse(contentRevisionId);
        });
        storeManager.dispatch(
            responseActions.removeResponses(allQuestionsInReadinessCheck)
        );
    },

    startReadinessCheckAndNavigate(contentRevisionId) {
        if (!sessionReader.isParentTest() && sessionReader.hasReadinessCheck()) {
            // Need to preserve the current question set as pending in order to be able to use it after the readiness check
            this.setStandaloneQuestionSetAsPending();
        }
        parentTestService.startReadinessCheck(contentRevisionId);
        apiService.notifyClientEvent(candidateProgressConstants.READINESS_CHECK_IN_PROGRESS, '', true);
        navigationService.navigateToFirstPage();
    },

    setStandaloneQuestionSetAsPending() {
        const { assessment, settings } = storeManager.getState();
        storeManager.dispatch(
            pendingAssessmentsActions.childAssessmentItemLoadSuccess(sessionReader.getAssessmentRunId(), assessment)
        );
        storeManager.dispatch(
            pendingAssessmentsActions.childTestSettingsLoadSuccess(sessionReader.getAssessmentRunId(), settings)
        );
    },

    restoreStandaloneQuestionSet() {
        const { assessment, pendingAssessments } = storeManager.getState();
        const assessmentRunId = sessionReader.getAssessmentRunId();
        if (assessment.assessmentRunId !== assessmentRunId && pendingAssessments[assessmentRunId]) {
            // The test set as the current assessment is not the assessment run and it is in pendingAssessments
            storeManager.dispatch(
                settingsActions.settingsLoadSuccess(pendingAssessments[assessmentRunId].settings)
            );
            storeManager.dispatch(
                assessmentActions.assessmentLoadSuccess(pendingAssessments[assessmentRunId].assessmentItem)
            );
        }
    },

    setChildContentRevisionId(contentRevisionId) {
        storeManager.dispatch(
            sessionActions.setChildTestIdentifiers({
                childTestIdentifier: {
                    type: 'questionSet',
                    paramName: 'contentRevisionId',
                    paramValue: contentRevisionId,
                },
                childContext: sessionConstants.CONTEXT_READINESS_CHECK,
            })
        );
        utilService.addUrlParams({
            childContentRevisionId: contentRevisionId,
            childContext: sessionConstants.CONTEXT_READINESS_CHECK,
        });
    },

    reportInitialReadinessCheckStateToMonitor() {
        if (this.isReadinessCheckComplete()) {
            this.reportDoneReadinessCheckToMonitor();
        } else {
            this.reportPendingReadinessCheckToMonitor();
        }
    },

    reportPendingReadinessCheckToMonitor() {
        apiService.notifyClientEvent(candidateProgressConstants.PENDING_READINESS_CHECK, '', true);
    },

    reportDoneReadinessCheckToMonitor() {
        apiService.notifyClientEvent(candidateProgressConstants.READY, '', true);
    },
};

export default readinessCheckService;
